import moment from 'moment'
import type { OptionsDocument } from 'paintscout'
// Deep import, not the '@paintscout/util/builder' barrel. That barrel carries a large web of
// circular imports, and pulling it into a leaf module like this one broke the Vite dev graph the
// last time it was tried (see the note the deleted dunningMessages.ts carried).
import { getObjectLabels } from '@paintscout/util/builder/util/get-object-labels'

/**
 * Every string the payment-failure surfaces show (ENG-1532), in one module so the dialog, the
 * billing-perms banner and the no-perms banner cannot drift apart. Copy is from the Figma
 * (Other Smaller Projects, node 2736-700).
 *
 * Two things are optional at every call site, and both are normal rather than exceptional:
 *
 *   - the deactivation date. Chargebee sends no `next_retry_at` on the final attempt, and the
 *     Auth0 token fallback carries `status` alone with no dates at all (see UserContext).
 *   - the amount. Same token fallback.
 *
 * So each helper drops the missing part and still reads as a sentence. Nothing here invents a
 * date from a local "cancel after 4 days" constant — that schedule lives in Chargebee.
 */

/** "July 25, 2026" — the format the Figma uses. */
export function formatDeactivationDate(deactivationAt?: number): string | undefined {
  if (!deactivationAt) return undefined

  return moment(deactivationAt).format('MMMM D, YYYY')
}

/**
 * "$119.00" from Chargebee's cents. Returns undefined rather than "$0.00" for a missing amount,
 * so callers can drop the clause instead of promising to charge nothing.
 */
export function formatAmountDue(amountDue?: number, currency = 'USD'): string | undefined {
  if (typeof amountDue !== 'number' || amountDue <= 0) return undefined

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amountDue / 100)
  } catch {
    // An unexpected currency code from Chargebee must not blank the whole payment screen.
    return `$${(amountDue / 100).toFixed(2)}`
  }
}

export const PAYMENT_FAILURE_DIALOG_TITLE = 'Payment Failure'
export const PAYMENT_FAILURE_HEADING = "We couldn't process your payment"

/**
 * The reassurance callout. The homeowner side never stops — that is the point of the issue.
 *
 * Two things vary. The object labels come from the account's own terminology, so a company that
 * renames Estimate to Proposal reads its own words. And the payments clause only appears when the
 * account actually has a payment integration connected: promising a customer can "make payments"
 * on an account with no processor names a capability that does not exist.
 *
 * `options` is optional so the copy still reads correctly before client options have loaded, in
 * which case getObjectLabels falls back to the stock Estimate/Invoice terminology.
 */
export function getCustomerNote({
  options,
  hasPayments
}: {
  options?: OptionsDocument
  hasPayments?: boolean
}): string {
  // Both labels live under the `quote` key — `invoice: true` switches getObjectLabels to the
  // invoice terminology rather than the estimate terminology.
  const quotes = getObjectLabels({ options }).quote.plural.toLowerCase()
  const invoices = getObjectLabels({ options, invoice: true }).quote.plural.toLowerCase()

  if (!hasPayments) {
    return `Your customers can still view ${quotes} and ${invoices}, and send messages as normal.`
  }

  return `Your customers can still view ${quotes} and ${invoices}, send messages, and make payments as normal.`
}

export const CONTACT_SUPPORT_LABEL = 'Contact Support'
export const PAY_NOW_LABEL = 'Pay Now'

/**
 * A run of copy, and whether it is emphasised.
 *
 * The Figma bolds the deadline date and the charge amount inside otherwise-plain sentences. These
 * helpers return segments rather than a single string so that emphasis lives with the copy instead
 * of being reconstructed by each caller with a string split — and so the module stays plain TS,
 * with no JSX, and stays directly testable.
 *
 * Render with `<CopyText segments={...} />` from `DunningCopyText`.
 */
export interface CopySegment {
  text: string
  bold?: boolean
}

/** The whole sentence as plain text — for tests, and for anywhere emphasis cannot be rendered. */
export function segmentsToText(segments: CopySegment[]): string {
  return segments.map((segment) => segment.text).join('')
}

/** Dialog body. Drops the date clause when Chargebee gave us no schedule. */
export function getPaymentFailureBody(deactivationAt?: number): CopySegment[] {
  const date = formatDeactivationDate(deactivationAt)

  if (!date) {
    return [
      {
        text: 'This may have just been an expired card or a bank issue. Update your payment details to keep your account active — otherwise it will be deactivated.'
      }
    ]
  }

  return [
    { text: 'This may have just been an expired card or a bank issue. Update your payment details by ' },
    { text: date, bold: true },
    { text: ' to keep your account active — after that, it will be deactivated.' }
  ]
}

/**
 * Step 2's charge line. Drops the amount when we do not have one to promise.
 *
 * Says the card is saved, which the Figma copy does not. `payOutstandingInvoice` passes
 * `replace_primary_payment_source: true`, so this card becomes the one Chargebee bills from then
 * on — replacing whatever was on file. Taking a payment method and silently making it the default
 * without saying so is not a thing to leave implied on a payment screen.
 */
export function getChargeNotice(amountDue?: number, currency?: string): CopySegment[] {
  const amount = formatAmountDue(amountDue, currency)

  if (!amount) {
    return [
      {
        text: 'Your card will be charged the outstanding balance and saved for future billing. Your plan will remain active.'
      }
    ]
  }

  return [
    { text: 'Your card will be charged ' },
    { text: amount, bold: true },
    { text: ' and saved for future billing. Your plan will remain active.' }
  ]
}

/** Header banner for a user who can fix it. */
export function getPastDueBannerText(deactivationAt?: number): CopySegment[] {
  const date = formatDeactivationDate(deactivationAt)

  if (!date) {
    return [{ text: 'You have an outstanding payment due. Update your payment details to keep your account active.' }]
  }

  return [
    { text: 'You have an outstanding payment due. Update your payment details by ' },
    { text: date, bold: true },
    { text: ' to keep your account active.' }
  ]
}

/**
 * Header banner for everyone else. No date and no amount by design — a user who cannot act on the
 * billing problem does not need the account's financial detail, only who to tell.
 */
export const PAST_DUE_BANNER_TEXT_NO_PERMISSION =
  "Your organization's payment is past due. Contact your account admin to avoid service interruption."

/** Deactivated block screen (Phase 5). */
export const DEACTIVATED_HEADING = 'Welcome back to PaintScout'
export const DEACTIVATED_BODY_WITH_PERMISSION =
  'Your account is deactivated, but your projects, customers, and data are all still here waiting for you. Reactivate to pick up right where you left off.'
export const DEACTIVATED_BODY_NO_PERMISSION =
  'Your account is deactivated, but your projects, customers, and data are all still here waiting for you. Contact your account admin to get started again.'
export const DEACTIVATED_BUTTON_LABEL = 'Select Plan'
